import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NumbersIcon from "@mui/icons-material/Numbers";

import { Reported } from "baptize-america-client";
import DebouncedButton from "./DebounceButton";
import { isNumber } from "../utils/helper";

interface UpdateCountDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: { updatedCount: Reported; reportedType: string }) => void;
  title: string;
  currentValue: number;
  previousReported: Reported;
  valueType: string;
  helperText: string;
}

const styles = {
  dialog: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  infoBox: {
    bgcolor: "rgba(123, 123, 123, 0.2)",
    p: 1,
    borderRadius: 2,
  },
};

const UpdateCountDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  currentValue,
  previousReported,
  valueType,
  helperText,
}: UpdateCountDialogProps) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [reportedCount, setReportedCount] = useState<string>(
    String(currentValue),
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const validateInput = () => {
    let error = "";
    if (!reportedCount?.trim()) {
      error = `${title} is required.`;
    } else if (!isNumber(reportedCount)) {
      error = `${title} must be a numerical value.`;
    } else if (isNumber(reportedCount) && Number(reportedCount) < 0) {
      error = `${title} must be greater than 0.`;
    }

    setErrorMessage(error);

    return error ? false : true;
  };

  const handleConfirm = () => {
    setErrorMessage("");
    if (!validateInput()) return;
    if (currentValue === Number(reportedCount)) {
      onClose();
      return;
    }

    const isUpdatingBaptismCount = valueType === "baptismPerformed";
    const updated = isUpdatingBaptismCount
      ? { ...previousReported, baptismPerformed: Number(reportedCount) }
      : { ...previousReported, salvationCount: Number(reportedCount) };

    onConfirm({
      updatedCount: updated,
      reportedType: isUpdatingBaptismCount
        ? "Number of Baptisms"
        : "Number of Salvations",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth={isLargeScreen ? "sm" : false}
      sx={styles.dialog}
    >
      <DialogTitle sx={{ textAlign: "center" }}>{`Total ${title}`}</DialogTitle>
      <DialogContent>
        <Box
          sx={[
            {
              "& .MuiTextField-root": { m: 1 },
            },
          ]}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          component="form"
          noValidate
          autoComplete="off"
        >
          <TextField
            required
            id={`total-${valueType}`}
            label={title}
            value={reportedCount}
            onChange={(e) => setReportedCount(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <NumbersIcon />
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
          />
          <Box sx={styles.infoBox}>
            <Typography
              align={"left"}
              sx={{ display: "flex", flexDirection: "row" }}
              component="div"
            >
              {helperText}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <DebouncedButton
          variant="contained"
          onClick={handleConfirm}
          sx={{ backgroundColor: "#00169E", color: "#FFF" }}
        >
          Update
        </DebouncedButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateCountDialog;
